/**
 * @description
 * Deployments page displaying deployment history and status for all clients.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 * @see admin/src/frontend/pages/Deployments.test.tsx
 */

import { Link } from 'react-router';
import { useState } from 'react';
import { useDeployments } from '../hooks/useDeployments';
import { useRunningDeployments } from '../hooks/useRunningDeployments';

/**
 * Deployments page component
 */
export function Deployments() {
  const { deployments, loading, refetch } = useDeployments();
  const { deployments: runningDeployments, loading: runningLoading, refetch: refetchRunning } = useRunningDeployments();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  /**
   * Sync all deployment statuses from process-manager to client databases
   */
  const handleSyncStatus = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      
      const response = await fetch('/api/deployments/sync-all-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to sync status');
      }
      
      const result = await response.json() as { synced: number; errors: number; message: string };
      setSyncMessage(`✓ Synced ${result.synced} deployments${result.errors > 0 ? `, ${result.errors} errors` : ''}`);
      
      // Refetch both deployment lists
      await Promise.all([refetch(), refetchRunning()]);
      
      // Clear message after 3 seconds
      setTimeout(() => setSyncMessage(null), 3000);
    } catch (error) {
      console.error('Error syncing status:', error);
      setSyncMessage('✗ Failed to sync status');
      setTimeout(() => setSyncMessage(null), 3000);
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Get the real-time status for a deployment by checking process-manager
   * This overrides the database status with the actual running status
   */
  const getRealTimeStatus = (clientId: string, dbStatus: string): string => {
    const runningDeployment = runningDeployments.find(d => d.clientId === clientId);
    
    // If process-manager reports it's running, use that status
    if (runningDeployment) {
      return runningDeployment.status;
    }
    
    // If database says running but process-manager doesn't have it, it's actually stopped
    if (dbStatus === 'running' || dbStatus === 'starting') {
      return 'stopped';
    }
    
    // Otherwise use the database status
    return dbStatus;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'running':
        return 'bg-green-100 text-green-800';
      case 'failed':
      case 'crashed':
        return 'bg-red-100 text-red-800';
      case 'deploying':
      case 'starting':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rolled_back':
        return 'bg-orange-100 text-orange-800';
      case 'stopped':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'running':
        return <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>;
      case 'failed':
      case 'crashed':
        return (
          <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'deploying':
      case 'starting':
        return <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>;
      case 'stopped':
        return (
          <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  if (loading && runningLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Deployments</h1>
          <p className="text-muted-foreground">
            View and manage deployment history across all clients
          </p>
        </div>
        <div className="flex items-center gap-3">
          {syncMessage && (
            <span className={`text-sm ${syncMessage.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>
              {syncMessage}
            </span>
          )}
          <button
            onClick={handleSyncStatus}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                Syncing...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync Status
              </>
            )}
          </button>
        </div>
      </div>

      {/* Running Deployments Section */}
      {runningDeployments.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Running Deployments</h2>
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
              {runningDeployments.length} Active
            </span>
          </div>
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Subdomain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Port
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      PID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Uptime
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {runningDeployments.map((deployment) => (
                    <tr key={deployment.clientId} className="transition-colors hover:bg-muted/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {deployment.status === 'running' ? (
                            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                          ) : (
                            <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                          )}
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            deployment.status === 'running'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {deployment.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium">{deployment.subdomain}</span>
                      </td>
                      <td className="px-6 py-4">
                        {deployment.port ? (
                          <a
                            href={`http://localhost:${deployment.port}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-mono text-sm"
                          >
                            {deployment.port}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm">{deployment.pid || '-'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {deployment.uptime ? `${Math.floor(deployment.uptime / 60)}m ${Math.floor(deployment.uptime % 60)}s` : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/clients/${deployment.clientId}`}
                          className="text-sm font-medium text-primary hover:underline"
                        >
                          View Client
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Deployment History Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Deployment History</h2>

      {/* Deployments List */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Deployed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Deployed At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {deployments.map((deployment) => {
                const realTimeStatus = getRealTimeStatus(deployment.clientId, deployment.status);
                return (
                <tr key={deployment.id} className="transition-colors hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(realTimeStatus)}
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(realTimeStatus)}`}>
                        {realTimeStatus}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Link
                      to={`/clients/${deployment.clientId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {deployment.clientName}
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm">{deployment.version}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {deployment.deployedBy}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {new Date(deployment.startedAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/deployment-details/${deployment.id}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */